import { useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { Box, Chip, Link, MenuItem, Stack, TextField } from '@mui/material';

import ComputerFrame from '@/designs-dev/device/ComputerFrame';
import { useScreenSelect } from '@/designs-dev/loft/ScreenSelectContext';
import AdviserView from '@/james-playground/aa-family-claim/AdviserView';
import { buildClaim } from '@/james-playground/aa-family-claim/AaFamilyClaim';
import { sent } from '@/james-playground/aa-family-claim/AaFamilyClaim.scenes';
import {
  SCENARIOS,
  type ScenarioId,
} from '@/james-playground/aa-family-claim/claim/scenarios';
import { STAGE } from '@/james-playground/aa-family-claim/AaFamilyClaim.content';
import { PLAN_PATH } from '@/james-playground/aa-family-claim/AaFamilyClaimStage';

/**
 * The Care Adviser's side of the same claim, behind Loft's User switch. It
 * opens on the claim the family's Sent scene sends, so the two sides of the
 * demo show one claim from both ends.
 */
const AdviserStage = () => {
  const screenSelect = useScreenSelect();
  const [searchParams] = useSearchParams();
  const [scenario, setScenario] = useState<ScenarioId>(() => {
    const asked = searchParams.get('scenario');
    return SCENARIOS.some((x) => x.id === asked) ? (asked as ScenarioId) : 'standard';
  });
  // The claim the family's Sent scene sends, for whichever pathway is picked.
  const claim = useMemo(() => buildClaim({ scenario, prepare: sent }), [scenario]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: 'custom.neutral.100',
        p: 3,
        gap: 2,
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 1,
        }}
        direction='row'
      >
        {screenSelect}
        <TextField
          select
          size='small'
          label={STAGE.scenario}
          // Full strength: Loft's grey label on the stage's cream measured 3.31:1.
          slotProps={{ inputLabel: { sx: { color: 'text.primary' } } }}
          value={scenario}
          onChange={(e) => setScenario(e.target.value as ScenarioId)}
          sx={{ minWidth: 180 }}
        >
          {SCENARIOS.map((x) => (
            <MenuItem key={x.id} value={x.id}>
              {x.label}
            </MenuItem>
          ))}
        </TextField>
        <Chip size='small' variant='outlined' label={STAGE.badge} />
        <Link component={RouterLink} to={PLAN_PATH} variant='body2'>
          {STAGE.about}
        </Link>
      </Stack>
      <ComputerFrame>
        <AdviserView claim={claim} />
      </ComputerFrame>
    </Box>
  );
};

export default AdviserStage;
